import { Account, NATIVE_MINT, TOKEN_PROGRAM_ID, unpackAccount } from '@solana/spl-token';
import { AccountInfo, Connection, PublicKey } from '@solana/web3.js';
import * as pumpfun from '../../pumpfun/pumpfun';
import { LAUNCHPAD_PROGRAM, LaunchpadPool, getPdaLaunchpadPoolId } from '@raydium-io/raydium-sdk-v2';
import { toast } from 'react-toastify';

async function getAccountsChunked(connection: Connection, accounts: PublicKey[], chunkSize: number = 100) {
    const chunks = [];
    for (let i = 0; i < accounts.length; i += chunkSize) {
        chunks.push(accounts.slice(i, i + chunkSize));
    }

    const results = [];
    for (const chunk of chunks) {
        const resp = await connection.getMultipleAccountsInfo(chunk, { commitment: 'processed' });
        results.push(...resp);
    }

    return results;
}

function notNull<T>(value: T | null): value is T {
    return value !== null;
}

export async function getWalletsTokens(connection: Connection, wallets: PublicKey[]) {
    const accountsPKs: PublicKey[] = [];
    for (const wallet of wallets)
        accountsPKs.push(
            ...(await connection.getTokenAccountsByOwner(wallet, { programId: TOKEN_PROGRAM_ID }, { commitment: 'processed' })).value.map((acc) => acc.pubkey),
        );

    const accountsInfo = (await getAccountsChunked(connection, accountsPKs)).filter(notNull);
    const seen = new Set<string>();
    const accounts = accountsInfo
        .map((acc) => unpackAccount(acc.owner, acc, TOKEN_PROGRAM_ID))
        .filter((account) => account.amount > 0n)
        .filter((account) => {
            const mint = account.mint.toBase58();
            if (seen.has(mint)) return false;
            seen.add(mint);
            return true;
        });

    type BondingCurveRequest = {
        account: Account;
        pumpPubkey: PublicKey;
        bonkPubkey: PublicKey;
        pumpInfo?: AccountInfo<Buffer> | null;
        bonkInfo?: AccountInfo<Buffer> | null;
    };
    const bondingCurveRequests: BondingCurveRequest[] = accounts.map((account) => {
        const mint = new PublicKey(account.mint);

        return {
            account,
            pumpPubkey: pumpfun.getBondingCurvePubkey(mint),
            bonkPubkey: getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, mint, NATIVE_MINT).publicKey,
        };
    });
    const [pumpInfos, bonkInfos] = await Promise.all([
        getAccountsChunked(
            connection,
            bondingCurveRequests.map((x) => x.pumpPubkey),
        ),
        getAccountsChunked(
            connection,
            bondingCurveRequests.map((x) => x.bonkPubkey),
        ),
    ]);

    bondingCurveRequests.forEach((req, i) => {
        req.pumpInfo = pumpInfos[i];
        req.bonkInfo = bonkInfos[i];
    });

    const withInfos = bondingCurveRequests.filter((x) => x.pumpInfo !== null || x.bonkInfo !== null);
    const pumpPools = withInfos.filter((x) => x.pumpInfo !== null).map((x) => pumpfun.decodeBondingCurve(x.pumpInfo!));

    const bonkPools = withInfos.filter((x) => x.bonkInfo !== null).map((x) => LaunchpadPool.decode(x.bonkInfo!.data));

    toast.info(`Found ${pumpPools.length} pump pools and ${bonkPools.length} bonk pools`);

    const creators = new Set(wallets.map((w) => w.toBase58()));

    const tokensWithPools = withInfos.map((x) => ({
        account: x.account,
        pumpPool: x.pumpInfo ? pumpfun.decodeBondingCurve(x.pumpInfo) : null,
        bonkPool: x.bonkInfo ? LaunchpadPool.decode(x.bonkInfo.data) : null,
    }));

    const deployedTokens = tokensWithPools.filter((t) => t.pumpPool && creators.has(t.pumpPool.creator.toBase58())).map((t) => new PublicKey(t.account.mint));

    const purchasedTokens = tokensWithPools
        .filter((t) => !t.pumpPool || !creators.has(t.pumpPool.creator.toBase58()))
        .map((t) => new PublicKey(t.account.mint));

    return {
        deployedTokens,
        purchasedTokens,
    };
}
